import { API_BASE } from "@/config";

export const TOKEN_KEY = "assignoo.auth.token";
export const REFRESH_KEY = "assignoo.auth.refresh";
export const WORKSPACE_KEY = "assignoo.workspace.id";

export const storage = {
  get: (k, d = "") => { try { const v = localStorage.getItem(k); return v === null ? d : v; } catch { return d; } },
  set: (k, v) => { try { localStorage.setItem(k, v); } catch { /* noop */ } },
  remove: (k) => { try { localStorage.removeItem(k); } catch { /* noop */ } },
};

export const getToken = () => storage.get(TOKEN_KEY, "");
export const setToken = (t) => storage.set(TOKEN_KEY, t);
export const clearToken = () => storage.remove(TOKEN_KEY);
export const getRefreshToken = () => storage.get(REFRESH_KEY, "");
export const setRefreshToken = (t) => storage.set(REFRESH_KEY, t);
export const clearRefreshToken = () => storage.remove(REFRESH_KEY);

function buildQuery(query) {
  if (!query) return "";
  const params = new URLSearchParams();
  for (const [k, v] of Object.entries(query)) {
    if (v === undefined || v === null || v === "") continue;
    params.append(k, String(v));
  }
  const s = params.toString();
  return s ? `?${s}` : "";
}

async function tryRefresh() {
  const refresh = getRefreshToken();
  if (!refresh) return false;
  try {
    const res = await fetch(`${API_BASE}/auth/refresh`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ refresh_token: refresh }),
    });
    if (!res.ok) return false;
    const data = await res.json();
    if (data.access_token) { setToken(data.access_token); return true; }
    return false;
  } catch {
    return false;
  }
}

function detailToString(d, status) {
  if (!d) return `Request failed (${status})`;
  if (typeof d === "string") return d;
  if (Array.isArray(d)) return d.map((x) => x?.msg || JSON.stringify(x)).join(", ");
  if (typeof d === "object") return d.message || d.msg || JSON.stringify(d);
  return String(d);
}

export async function api(path, opts = {}) {
  const { method = "GET", body, query, auth = true, _retried } = opts;
  const headers = { "Content-Type": "application/json" };
  if (auth) {
    const token = getToken();
    if (token) headers.Authorization = `Bearer ${token}`;
  }
  let res;
  try {
    res = await fetch(`${API_BASE}${path}${buildQuery(query)}`, {
      method, headers, body: body ? JSON.stringify(body) : undefined,
    });
  } catch {
    throw new Error("Network error — please check your connection.");
  }
  if (res.status === 401 && auth && !_retried) {
    const ok = await tryRefresh();
    if (ok) return api(path, { ...opts, _retried: true });
  }
  const text = await res.text();
  let data = null;
  try { data = text ? JSON.parse(text) : null; } catch { data = text; }
  if (!res.ok) throw new Error(detailToString(data && data.detail, res.status));
  return data;
}

/** Authenticated binary download (Excel exports). */
export async function downloadFile(path, filename) {
  const token = getToken();
  const res = await fetch(`${API_BASE}${path}`, { headers: token ? { Authorization: `Bearer ${token}` } : {} });
  if (!res.ok) {
    let msg = `Export failed (HTTP ${res.status})`;
    try { const j = await res.json(); if (j?.detail) msg = String(j.detail); } catch { /* noop */ }
    throw new Error(msg);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.rel = "noopener";
  document.body.appendChild(a); a.click();
  setTimeout(() => { URL.revokeObjectURL(url); a.remove(); }, 500);
}

/** Web replacement for RN Share.share — native share sheet or clipboard fallback. */
export async function shareText({ message, title, url }) {
  if (navigator.share) {
    try { await navigator.share({ text: message, title, url }); return "shared"; } catch (e) { if (e?.name === "AbortError") return "cancelled"; }
  }
  await copyText(url ? `${message}\n${url}` : message);
  return "copied";
}

export async function copyText(text) {
  try { await navigator.clipboard.writeText(text); return true; } catch {
    const ta = document.createElement("textarea");
    ta.value = text; document.body.appendChild(ta); ta.select();
    try { document.execCommand("copy"); } catch { /* noop */ }
    ta.remove();
    return true;
  }
}

/** Read a picked image file → { dataUrl, base64, mimeType }. */
export function readImageFile(file) {
  return new Promise((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const dataUrl = String(r.result);
      resolve({ dataUrl, base64: dataUrl.split(",")[1] || "", mimeType: file.type || "image/jpeg" });
    };
    r.onerror = reject;
    r.readAsDataURL(file);
  });
}

/** Downscale image to keep base64 payloads small (mirrors quality 0.4–0.6 on mobile). */
export function compressImage(file, maxSize = 800, quality = 0.6, square = false) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const url = URL.createObjectURL(file);
    img.onload = () => {
      let { width: w, height: h } = img;
      let sx = 0, sy = 0, sw = w, sh = h;
      if (square) { const m = Math.min(w, h); sx = (w - m) / 2; sy = (h - m) / 2; sw = sh = m; w = h = m; }
      const scale = Math.min(1, maxSize / Math.max(w, h));
      const cw = Math.round(w * scale), ch = Math.round(h * scale);
      const c = document.createElement("canvas");
      c.width = cw; c.height = ch;
      c.getContext("2d").drawImage(img, sx, sy, sw, sh, 0, 0, cw, ch);
      const dataUrl = c.toDataURL("image/jpeg", quality);
      URL.revokeObjectURL(url);
      resolve({ dataUrl, base64: dataUrl.split(",")[1], mimeType: "image/jpeg" });
    };
    img.onerror = (e) => { URL.revokeObjectURL(url); reject(e); };
    img.src = url;
  });
}

export function pickImage() {
  return new Promise((resolve) => {
    const input = document.createElement("input");
    input.type = "file"; input.accept = "image/*";
    input.onchange = () => resolve(input.files?.[0] || null);
    input.click();
  });
}
